import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  createAgentSearch,
  dismissAgentSearch,
  type ClipboardAgentSearch,
} from "@/features/clipboard/api/clipboard";
import {
  invalidateClipboardAgentSearches,
  useClipboardAgentSearchesQuery,
} from "@/features/clipboard/clipboardQueries";
import { useClipboardRoute } from "@/features/clipboard/hooks/useClipboardRoute";
import { UsageAlert } from "@/features/usage/components/UsageAlert";
import { useDailyPrompts } from "@/features/usage/hooks/useLimit";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

type Props = {
  onError: (message: string) => void;
};

const PENDING_STATUSES = ["pending", "dispatched", "running", "executing", "retry"];

/**
 * The "ask your Clipboard" composer. Docked to the bottom of the Clipboard
 * page (sticky inside the scroll column) so it floats under the file list
 * the way the chat composer floats under a conversation.
 */
export const ClipboardAskBar = ({ onError }: Props) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { path, routeSearchId } = useClipboardRoute();
  const [agentSearchText, setAgentSearchText] = useState("");
  const [startingSearch, setStartingSearch] = useState(false);
  const {
    isAtLimit: promptsAtLimit,
    isApproachingLimit: promptsApproaching,
    limit: promptsLimit,
  } = useDailyPrompts();

  const runAgentSearch = async (event: React.FormEvent) => {
    event.preventDefault();
    const query = agentSearchText.trim();
    if (!query || startingSearch || promptsAtLimit) return;
    setStartingSearch(true);
    try {
      const search = await createAgentSearch(query, routeSearchId ? "" : path);
      setAgentSearchText("");
      await invalidateClipboardAgentSearches(queryClient);
      navigate(`/clipboard/search/${search.id}`);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not start search");
    } finally {
      setStartingSearch(false);
    }
  };

  return (
    <div className="relative sticky bottom-0 z-10 shrink-0 bg-white px-8 pt-4 pb-5">
      {/* Same mask fade as session titles, vertical: list ink dissolves into
          the composer. bg-white follows --color-white (light / dark / beige). */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-10 h-10 bg-white"
        style={{
          maskImage: "linear-gradient(to top, black, transparent)",
          WebkitMaskImage: "linear-gradient(to top, black, transparent)",
        }}
      />
      <UsageAlert
        id="usage-alert-clipboard-prompts"
        name="dailyPrompts"
        isAtLimit={promptsAtLimit}
        isApproachingLimit={promptsApproaching}
        limit={promptsLimit}
      />
      <form
        onSubmit={(event) => void runAgentSearch(event)}
        className="prompt-box--editorial flex min-w-0 max-w-3xl items-center gap-4"
        aria-describedby={
          promptsAtLimit || promptsApproaching ? "usage-alert-clipboard-prompts" : undefined
        }
      >
        <input
          type="text"
          placeholder={
            promptsAtLimit ? "Daily query limit reached" : "Ask your Clipboard a question, or grep it…"
          }
          value={agentSearchText}
          onChange={(event) => setAgentSearchText(event.target.value)}
          disabled={promptsAtLimit}
          className="clip-search-input min-w-0 flex-1 bg-transparent text-base leading-7 text-zinc-900 outline-none placeholder:italic placeholder:text-zinc-400"
          aria-label="Search clipboard"
        />
        <button
          type="submit"
          disabled={promptsAtLimit || startingSearch || agentSearchText.trim().length < 3}
          className="font-ui shrink-0 text-sm text-zinc-500 transition-colors hover:text-zinc-900 disabled:pointer-events-none disabled:opacity-40"
        >
          {startingSearch ? "Starting…" : "Search ↑"}
        </button>
      </form>
    </div>
  );
};

/** Recent questions, set as a running line above the folder breadcrumb. */
export const ClipboardRecentSearches = ({ onError }: Props) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { routeSearchId } = useClipboardRoute();
  const searchesQuery = useClipboardAgentSearchesQuery(!!user);
  const searches = searchesQuery.data ?? [];

  const closeAgentSearch = async (
    event: React.MouseEvent,
    search: ClipboardAgentSearch,
  ) => {
    event.stopPropagation();
    try {
      await dismissAgentSearch(search.id);
      await invalidateClipboardAgentSearches(queryClient);
      if (routeSearchId === search.id) navigate("/clipboard", { replace: true });
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not close search");
    }
  };

  if (searches.length === 0) return null;

  return (
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1 text-sm leading-6">
      <span className="font-ui mr-1 text-zinc-400">Recent</span>
      <button
        type="button"
        onClick={() => navigate("/clipboard")}
        className={`font-ui transition-colors hover:text-zinc-900 ${!routeSearchId ? "text-zinc-900" : "text-zinc-400"
          }`}
      >
        All files
      </button>
      {searches.map((search) => {
        const pending = PENDING_STATUSES.includes(search.status);
        const active = routeSearchId === search.id;
        return (
          <span key={search.id} className="group flex max-w-72 min-w-0 items-baseline">
            <span aria-hidden className="mx-1 text-zinc-300">·</span>
            <button
              type="button"
              onClick={() => navigate(`/clipboard/search/${search.id}`)}
              title={search.query}
              className={`min-w-0 truncate transition-colors hover:text-zinc-900 ${active ? "text-zinc-900" : "text-zinc-400"
                } ${pending ? "italic" : ""}`}
            >
              {search.query}
              {!pending && (
                <span className="ml-1 text-xs text-zinc-400">{search.result_count}</span>
              )}
            </button>
            <button
              type="button"
              aria-label={`Close search ${search.query}`}
              onClick={(event) => void closeAgentSearch(event, search)}
              className="ml-1 text-zinc-300 opacity-0 transition-opacity hover:text-zinc-700 group-hover:opacity-100 focus-visible:opacity-100"
            >
              ×
            </button>
          </span>
        );
      })}
    </div>
  );
};
